import { useCallback, useEffect, useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import type { StripeElementsOptions, StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { useTranslation } from '@frontend/i18n';
import { stripeAppearance, useStripePromise } from '@frontend/payments-stripe-wrapper';
import { BaseFieldProps, contextFactory } from '@frontend/design-system';
import { usePaymentMethodSetupStripe } from '../hooks/use-payment-method-setup-stripe';
import type { PaymentMethodSetupKind, StripeSetupGatewayConfig } from '../types';
import {
  buildNoticeValue,
  PaymentMethodSetupContext,
  PaymentMethodSetupContextValue,
  PaymentMethodSetupGatewayProps,
} from './payment-method-setup-base.context';
import { PaymentMethodSetupUnsupportedProvider } from './payment-method-setup-unsupported.context';

const STRIPE_PAYMENT_METHOD_TYPES: Record<PaymentMethodSetupKind, string[]> = {
  card: ['card'],
  ach: ['us_bank_account'],
};

export type PaymentMethodSetupStripeContextValue = {
  /** Cards only. */
  nameFieldProps: BaseFieldProps<string, '', HTMLInputElement>;
  onElementChange: (event: StripePaymentElementChangeEvent) => void;
  onElementReady: () => void;
  onElementLoadError: () => void;
};

export const [PaymentMethodSetupStripeContext, usePaymentMethodSetupStripeContext] =
  contextFactory<PaymentMethodSetupStripeContextValue>(
    'usePaymentMethodSetupStripeContext must be used inside PaymentMethodSetupStripeProvider'
  );

const buildStripeSetupElementsOptions = (
  kind: PaymentMethodSetupKind,
  config: StripeSetupGatewayConfig,
  connectedAccountId: string | undefined
): StripeElementsOptions => {
  const paymentMethodConfiguration = config.paymentMethodConfiguration?.[kind];

  return {
    mode: 'setup',
    currency: 'usd',
    setupFutureUsage: 'off_session',
    ...(paymentMethodConfiguration
      ? { paymentMethodConfiguration }
      : { paymentMethodTypes: STRIPE_PAYMENT_METHOD_TYPES[kind] }),
    onBehalfOf: connectedAccountId || undefined,
    ...(kind === 'ach' && { paymentMethodOptions: { us_bank_account: { verification_method: 'instant' } } }),
    appearance: config.appearance ?? stripeAppearance,
  };
};

export const PaymentMethodSetupStripeProvider = (props: PaymentMethodSetupGatewayProps) => {
  const config = props.gatewayConfig.stripe;
  if (!config?.publishableKey) return <PaymentMethodSetupUnsupportedProvider {...props} />;

  return <StripeSetupElements {...props} config={config} />;
};

/** Mounted per open so the element starts fresh. */
const StripeSetupElements = ({
  config,
  ...props
}: PaymentMethodSetupGatewayProps & { config: StripeSetupGatewayConfig }) => {
  const { stripePromise } = useStripePromise({ stripeExpressKey: config.publishableKey });
  const { kind, session } = props;

  const options = useMemo(
    () => buildStripeSetupElementsOptions(kind, config, session.connectedAccountId),
    [config, kind, session.connectedAccountId]
  );

  return (
    <Elements key={`${config.publishableKey}-${kind}`} stripe={stripePromise} options={options}>
      <PaymentMethodSetupStripeBody {...props} />
    </Elements>
  );
};

const PaymentMethodSetupStripeBody = ({ children, ...props }: PaymentMethodSetupGatewayProps) => {
  const { t } = useTranslation('payments');
  const { kind, onCancel, onEvent, onSavingChange } = props;
  const { save, isSaving, canSave, nameFieldProps, onElementChange } = usePaymentMethodSetupStripe(props);
  const [isElementReady, setIsElementReady] = useState(false);

  useEffect(() => {
    onSavingChange?.(isSaving);
    return () => onSavingChange?.(false);
  }, [isSaving, onSavingChange]);
  const [hasLoadError, setHasLoadError] = useState(false);

  const onElementReady = useCallback(() => setIsElementReady(true), []);
  const onElementLoadError = useCallback(() => {
    onEvent('setup_session_failed', { code: 'element_load_error' });
    setHasLoadError(true);
  }, [onEvent]);

  const value = useMemo<PaymentMethodSetupContextValue>(
    () =>
      hasLoadError
        ? buildNoticeValue({
            kind,
            gateway: 'stripe',
            message: t("We couldn't start secure payment setup. Please try again."),
            onCancel,
            t,
          })
        : {
            kind,
            view: 'form',
            gateway: 'stripe',
            primary: { label: t('Save'), onClick: save, disabled: !isElementReady || !canSave || isSaving },
            secondary: { label: t('Cancel'), onClick: onCancel, disabled: isSaving },
            shouldShowLoading: isSaving,
            loadingMessage: kind === 'card' ? t('Saving card...') : t('Saving ACH...'),
          },
    [canSave, hasLoadError, isElementReady, isSaving, kind, onCancel, save, t]
  );

  const stripeValue = useMemo<PaymentMethodSetupStripeContextValue>(
    () => ({
      nameFieldProps,
      onElementChange,
      onElementReady,
      onElementLoadError,
    }),
    [nameFieldProps, onElementChange, onElementLoadError, onElementReady]
  );

  return (
    <PaymentMethodSetupContext.Provider value={value}>
      <PaymentMethodSetupStripeContext.Provider value={stripeValue}>
        {children}
      </PaymentMethodSetupStripeContext.Provider>
    </PaymentMethodSetupContext.Provider>
  );
};
